import React, { useMemo, useState } from 'react';
import { CFColorTokenProp } from '~/theme/tokens/contentful-color';

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  isScrolled?: boolean;
  cfButtonColor?: CFColorTokenProp;
  cfButtonHoverColor?: CFColorTokenProp;
};

const CF_COLORS: Record<string, string> = {
  spearmint: '#80D6B0',
  green: '#3BBFAD',
  ginger: '#F4BA42',
  blackberry: '#1D252C',
  cinnamint: '#D66755',
  sweetmint: '#035C67',
  white: '#FFFFFF',
  mutedNavy: '#9AB5B8',
  transparent: 'transparent',
  limegreen: '#BBF174',
  callout: '#CDE012'
};

function getTokenKey(
  token?: CFColorTokenProp
): keyof typeof CF_COLORS | undefined {
  if (!token) return undefined;
  if (typeof token === 'string') return token as keyof typeof CF_COLORS;
  const obj = token as Record<string, keyof typeof CF_COLORS | undefined>;
  return (
    obj['0'] ?? obj.min ?? obj.mobile ?? obj.tablet ?? obj.desktop ?? obj.max
  );
}

function resolveBg(token?: CFColorTokenProp, fallback?: string) {
  const key = getTokenKey(token);
  return key ? CF_COLORS[key] ?? fallback : fallback;
}

function resolveText(token?: CFColorTokenProp) {
  const dark = new Set(['blackberry', 'sweetmint']);
  const key = getTokenKey(token);
  return key && dark.has(key) ? '#FFFFFF' : '#1D252C';
}

export function GetStartedButton({
  isScrolled,
  cfButtonColor = 'ginger',
  cfButtonHoverColor = 'green',
  className,
  style,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const [hovered, setHovered] = useState(false);
  const baseBg = useMemo(
    () => resolveBg(cfButtonColor, CF_COLORS.ginger),
    [cfButtonColor]
  );
  const hoverBg = useMemo(
    () => resolveBg(cfButtonHoverColor, baseBg),
    [cfButtonHoverColor, baseBg]
  );
  const bg = hovered ? hoverBg : baseBg;
  const text = resolveText(hovered ? cfButtonHoverColor : cfButtonColor);

  const width = isScrolled ? 'w-[104px]' : 'w-[286px]';
  const height = isScrolled ? 'h-10' : 'h-14';
  const font = isScrolled ? 'text-[14px]' : 'text-[16px]';

  return (
    <button
      {...rest}
      className={[
        'z-[1]',
        'flex-auto',
        'self-center',
        'inline-flex',
        'items-center',
        'justify-center',
        'whitespace-nowrap',
        'overflow-hidden',
        'rounded-[8px]',
        'min-h-[32px]',
        width,
        height,
        font,
        // desktop parity overrides
        'desktop:h-14',
        'desktop:text-[16px]',
        isScrolled ? 'desktop:w-[104px]' : 'desktop:w-[286px]',
        'transition-all',
        'duration-300',
        'ease-[cubic-bezier(0,0.7,0.3,1.01)]',
        className || ''
      ].join(' ')}
      style={{
        backgroundColor: bg,
        color: text,
        WebkitTapHighlightColor: 'transparent',
        willChange: 'width, flex-grow',
        minWidth: 'fit-content',
        WebkitTransform: 'translate3d(0, 0, 0)',
        transform: 'translate3d(0, 0, 0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        WebkitPerspective: '1000',
        perspective: '1000',
        ...style
      }}
      onMouseEnter={(e) => {
        setHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
      }}
      onFocus={(e) => {
        setHovered(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setHovered(false);
        onBlur?.(e);
      }}
    >
      {children}
    </button>
  );
}

export default GetStartedButton;
